<!-- Component can be used in microscope and periscope -->
<template>
  <LabelAndHint
    v-slot="{ inputId, ariaLabelledby, isSuccess, isWarning, isErr }"
    v-bind="{ ...props, error: props.error || invalid, errorMessages: props.errorMessages || errorMessages }"
  >
    <div
      class="textarea-wrapper"
      :class="{
        'input-success': isSuccess,
        'input-warning': isWarning,
        'input-error': isErr,
        'input-disabled': disabled,
        slim: slim,
      }"
    >
      <textarea
        :id="inputId"
        ref="textareaElement"
        :value="currentValue ?? ''"
        :aria-labelledby="ariaLabelledby"
        inputmode="text"
        :rows="rows"
        :placeholder="placeholder"
        :disabled="disabled"
        @input="onInput"
        @keydown="emit('keydown', $event)"
        @focus="emit('focus')"
        @blur="onBlur"
      />
    </div>
  </LabelAndHint>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'

import type { ValidationRuleObject } from '@staff/shared'

import { useInputValidation } from '../../composables'
import LabelAndHint from '../AppTextField/LabelAndHint.vue'

const props = defineProps<{
  id?: string | number

  // For shared components to work in both vue 2.7 and vue 3 we need to support both `value` and `modelValue`
  // When periscope is updated to vue 3, we can remove `value` and only use `modelValue`
  value?: string | null
  modelValue?: string | null

  label?: string
  placeholder?: string

  hint?: string | string[]
  hideDetails?: boolean

  success?: boolean
  successMessages?: string | string[]

  warning?: boolean
  warningMessages?: string | string[]

  error?: boolean
  errorMessages?: string | string[]

  disabled?: boolean
  labelForMultipleInputs?: boolean

  rows?: number
  slim?: boolean

  rules?: (ValidationRuleObject | null)[]
  validationOptions?: {
    validateOnModelChange?: boolean
  }
}>()

const emit = defineEmits<{
  (e: 'input', value: string): void
  (e: 'update:modelValue', value: string): void
  (e: 'focus'): void
  (e: 'blur'): void
  (e: 'keydown', value: KeyboardEvent): void
}>()

// For vue 2.7 and vue 3 compatibility
const currentValue = computed(() => props.modelValue ?? props.value)

const textareaElement = ref<HTMLTextAreaElement>()

const { invalid, errorMessages, validate } = useInputValidation(
  () => currentValue.value,
  () => props.rules,
  textareaElement,
)

const onInput = (v: Event) => {
  if (!(v.target instanceof HTMLTextAreaElement)) {
    return
  }

  emit('input', v.target.value)
  emit('update:modelValue', v.target.value)

  if ((props.rules?.length ?? 0) > 0) {
    nextTick(() => validate())
  }
}

const onBlur = () => {
  emit('blur')

  if ((props.rules?.length ?? 0) > 0) {
    nextTick(() => validate())
  }
}

watch(
  () => currentValue.value,
  () => {
    if (invalid.value || props.validationOptions?.validateOnModelChange) {
      nextTick(() => validate())
    }
  },
)
</script>

<style lang="scss" scoped>
.textarea-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;

  border: 1px solid var(--color-border-default);
  border-radius: 6px;

  &::placeholder {
    color: var(--color-fg-subtle);
  }

  &:focus-within {
    border-color: var(--color-accent-fg);
    outline: 2px solid var(--color-accent-fg);
    outline-offset: -2px;

    &.input-error {
      outline: 2px solid var(--color-danger-fg);
    }
  }

  &.input-success {
    border-color: var(--color-textfield-success-border);
  }

  &.input-warning {
    border-color: var(--color-textfield-warning-border);
  }

  &.input-error {
    border-color: var(--color-danger-fg);
  }

  &.input-disabled {
    cursor: not-allowed;
    border-color: var(--color-textfield-disabled-border);

    textarea {
      &::placeholder {
        color: var(--color-fg-disabled);
      }
    }
  }

  &.slim {
    textarea {
      padding: 1px;
      font-size: 12px;
      border-radius: 3px;
    }
  }
}

textarea {
  display: block;
  width: 100%;
  padding: 6px 12px;

  color: var(--color-fg-default);
  font-size: 16px;

  background-color: transparent;
  border: none;

  &:focus-visible {
    outline: none;
  }
}
</style>
